import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "../styles/gradients.css";
import { SarasIQ, StarShape } from "./icons";

interface SplashScreenProps {
  isLoading: boolean;
  onFinish?: () => void;
  minDisplayTime?: number;
}

const SplashScreen = ({
  isLoading,
  onFinish,
  minDisplayTime = 2000,
}: SplashScreenProps) => {
  const [shouldDisplay, setShouldDisplay] = useState(true);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    // Always show the splash screen for the full minDisplayTime
    const timer = setTimeout(() => {
      // Only hide the splash screen if loading is also complete
      if (!isLoading) {
        setShouldDisplay(false);
        onFinish?.();
      }
    }, minDisplayTime);

    return () => clearTimeout(timer);
  }, [minDisplayTime, onFinish, isLoading]);

  // This effect handles the case where loading finishes after the minDisplayTime
  useEffect(() => {
    if (!isLoading && Date.now() - startTime >= minDisplayTime) {
      setShouldDisplay(false);
      onFinish?.();
    }
  }, [isLoading, minDisplayTime, onFinish, startTime]);

  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!shouldDisplay) {
      // Wait for animation to complete before removing from DOM
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 350); // Match the duration in the motion.div transition
      return () => clearTimeout(timer);
    }
  }, [shouldDisplay]);

  if (!isVisible) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-structural-white"
      initial={{ opacity: 1 }}
      animate={{ opacity: shouldDisplay ? 1 : 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      <div className="flex flex-col items-center justify-center space-y-8 px-4">
        {/* Logo */}
        <motion.div 
          className="h-20 w-20 mb-0"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            duration: 0.35,
            ease: "easeOut",
            delay: 0
          }}
        >
          <SarasIQ size={80} className="w-20 h-20 text-primary-500"/>
        </motion.div>
        
        {/* Text */}
        <div className="flex flex-col items-center gap-[0.75rem] text-structural-white">
          <motion.h1 
            className="text-[2.815rem] font-normal text-text-high-emphasis"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.35, ease: "easeOut", delay: 0.2 }}
          >
            Meet <span className="relative">
              <span 
                className="font-sourcesans text-5xl font-bold leading-8 text-gradient"
              >
                Saras iQ
              </span>
              <motion.span 
                className="absolute -right-3 -top-1 text-lg text-primary-500"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut", delay: 0.4 }}
              >
                <StarShape size={20} />
              </motion.span>
            </span>
          </motion.h1>
          <motion.p 
            className="font-sourcesans text-3xl font-normal italic text-text-high-emphasis text-center"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.35, ease: "easeOut", delay: 0.3 }}
          >
            Your e-commerce AI data analyst
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
};

export default SplashScreen;
